import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, useIntl } from 'react-intl';
import usePrevious from 'use-previous';

import { allowFreeText } from '@bibliocommons/utils-user';
import { catalogBibShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { SecondaryGhostButton } from '@bibliocommons/base-buttons';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import TextUgcEditor from '../TextUgcEditor';

import './QuotationEditor.scss';

const EDITOR_KEY = 'quotation';

export default function QuotationEditor(props) {
  const { open, currentUser, initialContent, catalogBib, quotationsTransactions, quotationId } = props;

  const intl = useIntl();
  const prevOpen = usePrevious(open);
  const addButtonRef = useRef();
  const editorRef = useRef();

  useEffect(() => {
    // Set focus on the form when it opens
    if (open && !prevOpen && editorRef.current) {
      setFocusWithinElement(editorRef.current);
    }

    // Set focus on the add button when the form is closed
    if (!open && prevOpen && addButtonRef.current) {
      setFocusWithinElement(addButtonRef.current);
    }
  }, [open, prevOpen, editorRef, addButtonRef]);

  const handleSuccess = () => {
    if (!props.quotationId) props.ugcEditorActions.closeEditor(EDITOR_KEY);
    if (props.handleSuccess) props.handleSuccess();
  };

  const handleCancel = () => {
    if (!props.quotationId) props.ugcEditorActions.closeEditor(EDITOR_KEY);
    if (props.handleCancel) props.handleCancel();
  };

  const addQuotation = quotationContent => {
    props.quotationsActions.addQuotation({
      metadataId: props.catalogBib.get('metadataId'),
      quotation: quotationContent,
      postSuccess: handleSuccess
    });
  };

  const updateQuotation = quotationContent => {
    props.quotationsActions.updateQuotation({
      metadataId: props.catalogBib.get('metadataId'),
      quotationId: props.quotationId,
      quotation: quotationContent,
      postSuccess: handleSuccess
    });
  };

  const renderCollapsed = () => {
    return (
      <SecondaryGhostButton
        ref={addButtonRef}
        dataKey="add-ugc"
        handleClick={() => props.ugcEditorActions.openEditor(EDITOR_KEY)}
      >
        <FormattedMessage id="add_quotation" />
      </SecondaryGhostButton>
    );
  };

  const renderExpanded = () => {
    const metadataId = catalogBib.get('metadataId');
    const isLoading =
      quotationsTransactions.getIn([metadataId, 'isAdding']) ||
      quotationsTransactions.getIn([metadataId, quotationId, 'isUpdating']);
    const label = quotationId
      ? intl.formatMessage({ id: 'edit_quotation_prompt' })
      : intl.formatMessage({ id: 'add_quotation_prompt' });

    return (
      <TextUgcEditor
        ref={editorRef}
        ugcType="quotation"
        handleConfirm={quotationId ? updateQuotation : addQuotation}
        handleCancel={handleCancel}
        label={label}
        maxLength={1000}
        isLoading={isLoading}
        initialContent={initialContent}
      />
    );
  };

  if (!currentUser || allowFreeText(currentUser)) {
    return <div className="cp-quotation-editor">{open ? renderExpanded() : renderCollapsed()}</div>;
  }

  return null;
}

QuotationEditor.defaultProps = {
  initialContent: ''
};

QuotationEditor.propTypes = {
  catalogBib: catalogBibShape,
  currentUser: userIdentityShape,
  quotationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleSuccess: PropTypes.func,
  handleCancel: PropTypes.func,
  initialContent: PropTypes.string,
  open: PropTypes.bool,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func),
  ugcEditorActions: PropTypes.objectOf(PropTypes.func)
};
